import {inject, Injectable} from '@angular/core';
import {Document} from '../../api/models/document';
import {ServerSelectionService} from '../server-selection/server-selection.service';
import {AuthService} from '../auth/auth.service';
import {DocumentStore} from '../../stores/document.store';
import {DialogService} from '../dialog/dialog.service';
import {SnackbarService} from '../snackbar/snackbar.service';
import {CheckedOutDocumentsService as ApiCheckedOutDocumentService} from '../../api/services/checked-out-documents.service';
import {DocumentsService as ApiDocumentsService} from '../../api/services/documents.service';
import {VaultsService as ApiVaultsService} from '../../api/services/vaults.service';
import {VaultStore} from '../../stores/vault.store';
import {TranslateService} from '@ngx-translate/core';
import {NavigationService} from '../navigation/navigation.service';
import {CheckoutEventService} from '../electron/checkout/checkout-event.service';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {VaultQuery} from '../../queries/vault.query';
import {PermissionService} from '../permission/permission.service';
import {DocumentQuery} from '../../queries/document.query';
import {SystemInfoService} from '../system-info/system-info.service';
import {environment} from '../../../environments/environment';
import {firstValueFrom} from 'rxjs';
import {take} from 'rxjs/operators';
import {getLocalVaultNameFormatByVault} from '../../util/local-aptera-vault-name-by-vault.util';
import {InProgressService} from '../in-progress/in-progress.service';
import {TabIdEnum} from '../../enums/tab-id.enum';
import {URL_PATH_CONFIGS} from '../../../configs/url-path.config';
import {HistoryService} from '../history/history.service';

@Injectable({
    providedIn: 'root'
})
export class CheckedOutDocumentService {
    private readonly apiCheckedOutDocumentService = inject(ApiCheckedOutDocumentService);
    private readonly apiDocumentsService = inject(ApiDocumentsService);
    private readonly apiVaultsService = inject(ApiVaultsService);
    private readonly authService = inject(AuthService);
    private readonly checkoutEventService = inject(CheckoutEventService);
    private readonly documentQuery = inject(DocumentQuery);
    private readonly documentStore = inject(DocumentStore);
    private readonly dialogService = inject(DialogService);
    private readonly snackbarService = inject(SnackbarService);
    private readonly historyService = inject(HistoryService);
    private readonly httpClient = inject(HttpClient);
    private readonly inProgressService = inject(InProgressService);
    private readonly navigationService = inject(NavigationService);
    private readonly permissionService = inject(PermissionService);
    private readonly serverSelectionService = inject(ServerSelectionService);
    private readonly systemInfoService = inject(SystemInfoService);
    private readonly translateService = inject(TranslateService);
    private readonly vaultQuery = inject(VaultQuery);
    private readonly vaultStore = inject(VaultStore);
    private readonly inProgressDirectory = environment.inProgressDirectory;

    public constructor() {
        this.checkoutEventService.fetchAndGetCheckedOutFileContentEvent.subscribe(async (documentId) => {
            const result = await this.fetchAndGetCheckedOutFileContent(documentId);
            this.checkoutEventService.fetchAndGetCheckedOutFileContentResultEvent.emit(result);
        });
    }

    public async fetchAndGetCheckedOutDocuments(vaultId?: string): Promise<Array<Document>> {
        let documents: Array<Document> = [];
        try {
            if (!vaultId) {
                documents = await firstValueFrom(this.apiCheckedOutDocumentService.CheckedOutDocumentsGetAll({
                    Authorization: await this.authService.getBearer(),
                }));
            } else {
                documents = await firstValueFrom(this.apiVaultsService.VaultsGetCheckedOutDocuments({
                    Authorization: await this.authService.getBearer(),
                    vaultId,
                }));
            }
            documents.sort((firstDocument: Document, secondDocument: Document) => {
                if (firstDocument.checkedOutSince && secondDocument.checkedOutSince) {
                    if (firstDocument.checkedOutSince > secondDocument.checkedOutSince) {
                        return -1;
                    }
                    if (firstDocument.checkedOutSince < secondDocument.checkedOutSince) {
                        return 1;
                    }
                }
                return 0;
            });
        } catch (err) {
            console.error(err);
            this.snackbarService.showError('DOCUMENTS_LOADING_ERROR_MSG');
        }

        return documents;
    }

    public async syncCheckedOutDocument(documentId: string): Promise<void> {
        if (!(this.systemInfoService.isElectron() && this.systemInfoService.isWindows())) {
            return;
        }

        this.checkoutEventService.syncLocalDocumentEvent.emit(documentId);
        await firstValueFrom(this.checkoutEventService.syncLocalDocumentResultEvent.pipe(take(1)));
    }

    public async openCheckedOutDocument(documentId: string): Promise<void> {
        try {
            if (this.systemInfoService.isElectron() && this.systemInfoService.isWindows()) {
                this.checkoutEventService.openLocalDocumentEvent.emit(documentId);
            } else {
                const document = await this.fetchAndGetCheckedOutDocumentById(documentId);
                this.checkoutEventService.downloadCheckedOutDocumentEvent.emit(document);
            }
        } catch (error) {
            console.error('document not found:', error);
        }
    }

    public async checkOutDocument(documentId: string): Promise<boolean> {
        let isCheckedOut = false;
        try {
            await firstValueFrom(this.apiDocumentsService.DocumentsCheckOut({
                documentId,
                Authorization: await this.authService.getBearer()
            }));
            isCheckedOut = true;
            const store = this.documentStore.getValue();
            if (store && store.entities && store.entities[documentId]) {
                await this.fetchAndGetCheckedOutDocuments(store.entities[documentId].vaultId);
            } else {
                throw new Error('no vault for document found');
            }
            await this.navigateAndOpenCheckedOutDocument(documentId);
        } catch (error) {
            const httpError = error as HttpErrorResponse;
            const errorCode = httpError?.error?.code;
            if (errorCode) {
                const errorMessage = this.translateService.instant('ERROR.CODES.CODE_' + errorCode);
                this.snackbarService.showError('ERROR.DOCUMENTS_CHECKOUT_MSG', httpError, { error: errorMessage });
            } else {
                this.snackbarService.showError('ERROR_DOCUMENT_CHECK_OUT', error as Error);
            }
            isCheckedOut = false;
        } finally {
            this.permissionService.removeDocumentPermissionCacheById(documentId);
        }
        return isCheckedOut;
    }

    public async checkInDocument(documentId: string): Promise<void> {
        let checkinResult = false;
        try {
            await firstValueFrom(this.apiCheckedOutDocumentService.CheckedOutDocumentsCheckIn({
                documentId,
                Authorization: await this.authService.getBearer()
            }));
            await this.removeItemFromListAndNavigate(documentId);
            checkinResult = true;
        } catch (error) {
            const httpError = error as HttpErrorResponse;
            const errorCode = httpError?.error?.code;
            if (errorCode) {
                const errorMessage = this.translateService.instant('ERROR.CODES.CODE_' + errorCode);
                this.snackbarService.showError('ERROR.DOCUMENTS_CHECKIN_MSG', httpError, { error: errorMessage });
            } else {
                this.snackbarService.showError('ERROR_DOCUMENT_CHECK_IN', error as Error);
            }
        } finally {
            this.permissionService.removeDocumentPermissionCacheById(documentId);
        }
        if (checkinResult) {
            await this.removeLocalFile(documentId);
        }
    }

    public async getCheckedOutDocumentByVaultIdAndName(vaultId: string, name: string): Promise<Document | undefined> {
        return (await this.fetchAndGetCheckedOutDocuments(vaultId))
            .find(document => document.name === name);
    }

    public async discardDocumentChanges(documentId: string): Promise<void> {
        const isConfirmedDiscard = await this.dialogService.showConfirmDialog({
            messageTranslationKey: 'DOCUMENT_DISCARD_CONFIRM_MSG',
            actionTranslationKey: 'ACTIONS.DOCUMENT.DISCARD',
        }, 'discard-document-changes');
        if (isConfirmedDiscard) {
            let discardResult = false;
            try {
                await firstValueFrom(this.apiCheckedOutDocumentService.CheckedOutDocumentsDelete({
                    documentId,
                    Authorization: await this.authService.getBearer()
                }));
                await this.removeItemFromListAndNavigate(documentId);
                discardResult = true;
            } catch (err) {
                console.error(err);
                this.snackbarService.showError('ERROR_DOCUMENT_REVERT_CHANGES');
            } finally {
                this.permissionService.removeDocumentPermissionCacheById(documentId);
            }
            if (discardResult) {
                await this.removeLocalFile(documentId);
            }
        }
    }

    public async setActive(documentId: string): Promise<void> {
        await this.permissionService.fetchCheckedOutDocumentPermission(documentId);
    }

    public async fetchAndGetCheckedOutDocumentById(documentId: string): Promise<Document> {
        const bearer = await this.authService.getBearer();
        return await firstValueFrom(this.apiCheckedOutDocumentService.CheckedOutDocumentsGet({
            Authorization: bearer,
            documentId: documentId
        }));
    }

    private async fetchAndGetCheckedOutFileContent(documentId: string, path: string = 'file'): Promise<ArrayBuffer | undefined> {
        const bearer = await this.authService.getBearer();
        try {
            // we can't use DocumentsGetOriginalFormatResponse as it tries to interpret result as json and fails
            const serverUrl = this.serverSelectionService.getInternalApiUrl();
            return await firstValueFrom(this.httpClient.get(`${serverUrl}/checked-out-documents/${documentId}/${path}`,
                {
                    responseType: 'arraybuffer',
                    headers: {
                        Authorization: bearer
                    }
                }));
        } catch (err) {
            this.snackbarService.showError('FILE_DOWNLOAD_ERROR_MSG', err as Error);
        }
        return;
    }

    private async navigateAndOpenCheckedOutDocument(documentId: string): Promise<void> {
        const activeIds = this.vaultStore.getValue().active;
        const activeId = activeIds.length > 0 ? activeIds[0] : '';
        if (activeId) {
            await this.inProgressService.fetchInProgressList(activeId);
            const inProgressDocumentPreviewLink = this.navigationService.getInProgressDocumentPreviewLink(activeId, documentId);
            this.navigationService.navigate(inProgressDocumentPreviewLink, { state: { preventNavigationToFirstListItem: true } })
                .then(async () => {
                    await this.syncCheckedOutDocument(documentId);
                    await this.openCheckedOutDocument(documentId);
                });
        }
    }

    private async removeItemFromListAndNavigate(currentDocumentId: string): Promise<void> {
        let link: Array<string> | undefined;
        let queryParams: Record<string, string> | undefined;

        const activeVaultId = this.vaultQuery.getFirstActiveVaultId() ?? '';
        const list = this.inProgressService.getInProgressList(activeVaultId);
        if (list && list.listItems.length > 1) {
            const documentIndex: number = list.listItems.findIndex(listItem => listItem.id === currentDocumentId);
            if (documentIndex !== -1) {
                const nextDocumentIndex = documentIndex === list.listItems.length - 1 ? 0 : documentIndex + 1;
                link = this.navigationService.getInProgressDocumentPreviewLink(activeVaultId, list.listItems[nextDocumentIndex].id);
                this.inProgressService.removeListItem(currentDocumentId);
            }
        }

        if (!link) {
            queryParams = {
                vaultId: activeVaultId,
                selectedTab: TabIdEnum.DOCUMENTS
            };
            link = [URL_PATH_CONFIGS.paths.magnets];
        }

        this.historyService.removeLatestHistoryItem();
        await this.navigationService.navigate(link, { queryParams });
    }

    private async removeLocalFile(documentId: string): Promise<boolean> {
        if (this.systemInfoService.isWebApp()) {
            return true;
        }
        const document = this.documentQuery.getDocumentById(documentId);
        if (document) {
            const vault = this.vaultQuery.getVaultById(document.vaultId);
            if (!vault) {
                return false;
            }

            const eventPromise = new Promise<boolean>((async resolve => {
                const deleteFileResult = await firstValueFrom(this.checkoutEventService.deleteFsFileEntryResultEvent);
                resolve(deleteFileResult);
            }));
            this.checkoutEventService.deleteFsFileEntryEvent.emit(`${getLocalVaultNameFormatByVault(vault)}/${this.inProgressDirectory}/${document.name}`);
            return await eventPromise;
        } else {
            return false;
        }
    }
}
